import React, { useEffect, useMemo, useRef, useState } from "react";
import SC from "../FileSearchInterface/style";
import { useSelector } from "react-redux";
import { apiGet, apiPost } from "api/callApi";
import MultiSelectDropdown from "components/AdvancedSearchComponent/MultiSelectDropdown";
import CreatedOnDropdown from "components/AdvancedSearchComponent/CreatedOnDropdown";
import MoreFilterDropdown from "components/AdvancedSearchComponent/MoreFilterDropdown";
import { projectType } from "utils/lookUpTable";
import SaveFilterModal from "components/AdvancedSearchComponent/SaveFilterModal";
import SavedFilterDropdown from "components/AdvancedSearchComponent/SavedFilterDropdown";
import { Button } from "@InnoUI/";
import theme from "@theme/";
import EmailFilterDropdown from "components/AdvancedSearchComponent/EmailFilterDropdown";
import OccupantAreaFilterDropdown from "components/AdvancedSearchComponent/OccupantAreaFilterDropdown";
import NumberFilterDropdown from "components/AdvancedSearchComponent/NumberFilterDropdown";
import OccupantTable from "./OccupantTable";
import { useSearchParams } from "react-router-dom";
import InnoIcons from "@InnoIcons/";
import { tenantReleaseStatus } from "utils/optionData/utils";

const OccupantSearchInterface = (props) => {
  const { setTotalRecord = () => {}, activeTab, filterSessionKey } = props;
  const [hideFilterOptions, setHideFilterOptions] = useState(false);
  const [data, setData] = useState([]);
  const [openModalView, setOpenModalView] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [emails, setEmails] = useState([]);
  const [contactEmails, setContactEmails] = useState([]);
  const [occupantData, setOccupantData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchKey, setSearchKey] = useState("");
  const [activeRelatedDropdowns, setActiveRelatedDropdowns] = useState([]);
  const [moreFilterSelectedIds, setMoreFilterSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const occupantsID = searchParams.get("projectId");

  const currentProject = useSelector(
    (state) => state?.currentProject?.data || [],
  );

  const occupantOptionData = useSelector((state) =>
    Array.isArray(state?.occupantOption?.data) ? state.occupantOption.data : [],
  );
  // Convert occupant data to {id, label} format for MainDropdown
  const options = occupantOptionData?.map((item) => ({
    id: item.id,
    label: `${item.occupantName} (${item.occupantNo})`,
  }));



  const optionsMoreFilter = [
    { id: "1", label: "Contact Name", related: "ContactNameDropdown" },
    { id: "2", label: "Contact Email", related: "ContactEmailDropdown" },
  ];

  const userOptions = data.map((item) => ({
    id: item.id,
    label: `${item.fullName}`,
  }));

  // const filterSessionKey = `${currentProject.id}-latestSearchFilter`;

  // Initialize selected filter ID
  const [selectedFilterId, setSelectedFilterId] = useState(() => {
    const saved = sessionStorage.getItem(filterSessionKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.selectedFilterId || null;
    }
    return null;
  });

  const defaultFilters = {
    occupants: { header: "equals", selectedIds: [] },
    tenantStatus: { header: "equals", selectedIds: [] },
    primaryContactId: { header: "equals", selectedIds: [] },
    lastUpdateBy: { header: "equals", selectedIds: [] },

    coordinatorEmail: { header: "equals", value: "", emails: [] },
    primaryContactEmail: { header: "equals", value: "", contactEmails: [] },
    occupantArea: { header: "equals", value: "" },
    countIssue: { header: "equals", value: "" },
    journalActive: { header: "equals", value: "" },

    occupantOpeningDate: {
      header: "withinLast",
      value: {
        number: "",
        unit: "",
      },
    },
    lastUpdate: null,
    textfields: "",
    rawJQL: [],
  };

  const [tabFilters, setTabFilters] = useState(() => {
    const saved = sessionStorage.getItem(filterSessionKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        ...{
          3: defaultFilters,
        },
        [parsed.activeTab]: parsed.filters || defaultFilters,
      };
    }
    return {
      3: defaultFilters,
    };
  });

  const [filters, setFilters] = useState(() => {
    const saved = sessionStorage.getItem(filterSessionKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.filters || defaultFilters;
    }
    return defaultFilters;
  });

  const [searchText, setSearchText] = useState(() => {
    const saved = sessionStorage.getItem(filterSessionKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.searchText || "";
    }
    return "";
  });

  // ✅ Count how many filters are applied
  const getAppliedFilterCount = (filters) => {
    let count = 0;

    // 1️⃣ Multi-select filters
    const multiSelectKeys = [
      "occupants",
      "tenantStatus",
      "primaryContactId",
      "lastUpdateBy",
    ];
    multiSelectKeys.forEach((key) => {
      if (filters[key]?.selectedIds?.length > 0) count++;
    });

    // 2️⃣ Email filters (arrays or non-empty strings)
    const emailKeys = ["coordinatorEmail", "primaryContactEmail"];
    emailKeys.forEach((key) => {
      const val = filters[key]?.value;
      if (Array.isArray(val) ? val.length > 0 : val?.trim()) count++;
    });

    // 3️⃣ Numeric / value filters
    const valueKeys = ["occupantArea", "countIssue", "journalActive"];
    valueKeys.forEach((key) => {
      if (filters[key]?.value) count++;
    });

    // 4️⃣ Date filters
    const dateKeys = ["occupantOpeningDate", "lastUpdate"];
    dateKeys.forEach((key) => {
      const val = filters[key];
      if (
        val &&
        ((val.value?.number && val.value?.unit) ||
          (val.value?.[0] && val.value?.[1]))
      ) {
        count++;
      }
    });

    // 5️⃣ Textfields search
    // if (filters.textfields?.trim()) count++;

    // 6️⃣ Raw JQL
    if (filters.rawJQL?.length > 0) count++;

    return count;
  };

  // 🧮 Compute it reactively
  const appliedFilterCount = useMemo(
    () => getAppliedFilterCount(filters),
    [filters],
  );

  useEffect(() => {
    if (appliedFilterCount > 0) {
      setHideFilterOptions(true);
    }
  }, [appliedFilterCount]);

  // Save filters only when applied by user
  useEffect(() => {
    const filterToStore = {
      filters,
      activeTab,
      searchText,
      selectedFilterId,
    };
    sessionStorage.setItem(filterSessionKey, JSON.stringify(filterToStore));
  }, [filters, activeTab, searchText, selectedFilterId]);

  useEffect(() => {
    const savedFilter = sessionStorage.getItem(filterSessionKey);
    if (savedFilter) {
      const parsed = JSON.parse(savedFilter);

      // Set state
      setFilters(parsed.filters || defaultFilters);
      setEmails(parsed.filters?.coordinatorEmail?.value || []);
      setContactEmails(parsed.filters?.primaryContactEmail?.value || []);
      //   setActiveTab(parsed.activeTab || "1");
      setSearchText(parsed.searchText || "");

      // Update tabFilters
      setTabFilters((prev) => ({
        ...prev,
        [parsed.activeTab || "1"]: parsed.filters || defaultFilters,
      }));
    }
  }, []);

  useEffect(() => {
    // Combine all text filter values for highlighting
    const textFilters = [
      filters.coordinatorEmail?.value,
      filters.primaryContactEmail?.value,
      filters.textfields,
    ].filter(Boolean); // remove empty strings or nulls

    const combinedSearchKey = textFilters.join(" ");
    setSearchKey(combinedSearchKey);
  }, [filters, searchText]);

  const fetchSavedFilters = async (tabId, type, occupantsID) => {
    if (!currentProject?.id) return;

    try {
      const payload = {
        projectId: currentProject.id,
        type: tabId, // 1=file, 2=journal, 3=occupant
      };
      const response = await apiPost("/v1/getfilters", payload, false);
      if (response?.data) {
        setSavedFilters(response.data);

        const sessionFilter = sessionStorage.getItem(filterSessionKey);
        let applyBackendDefault = false;

        if (!sessionFilter) {
          // Session empty → apply backend default
          applyBackendDefault = true;
        } else {
          const parsed = JSON.parse(sessionFilter);
          const sessionFilters = parsed.filters || {};
          const isSameAsDefault =
            JSON.stringify(sessionFilters) === JSON.stringify(defaultFilters);

          if (isSameAsDefault) {
            // Session has only default filters → backend default should apply
            applyBackendDefault = true;
          }
        }

        if (applyBackendDefault) {
          // 🟢 Backend default found → apply AS-IS (no occupant change)
          const defaultFilter = response.data.find((f) => f.isDefault);

          if (defaultFilter) {
            applyFilter(defaultFilter);
          } else {
            // ⚠️ No backend default → apply frontend default and inject occupantsID if type=1
            const modifiedDefault = {
              filters: {
                ...defaultFilters,
                ...(type == 1 && occupantsID
                  ? {
                      occupants: {
                        ...defaultFilters.occupants,
                        selectedIds: [occupantsID],
                      },
                    }
                  : {}),
              },
            };
            applyFilter(modifiedDefault);
          }
        } else {
          // 🟡 Session exists → update occupants if type=1
          const parsed = JSON.parse(sessionFilter);
          let updatedFilters = { ...parsed.filters };

          if (type == 1 && occupantsID) {
            updatedFilters = {
              ...updatedFilters,
              occupants: {
                ...updatedFilters.occupants,
                selectedIds: [occupantsID], // always replace with current occupant
              },
            };
          }

          const newSession = { ...parsed, filters: updatedFilters };
          setFilters(updatedFilters);
          sessionStorage.setItem(filterSessionKey, JSON.stringify(newSession));
        }
      }
    } catch (error) {
      console.error("Error fetching saved filters:", error);
      setSavedFilters([]);
    }
  };

  // Update applyFilter

  useEffect(() => {
    fetchSavedFilters(activeTab, type, occupantsID);
  }, [currentProject?.id, activeTab]);

  useEffect(() => {
    if (type == 1 && occupantsID) {
      fetchSavedFilters(activeTab, type, occupantsID);
    }
  }, [occupantsID]);

  const applyFilter = (filter) => {
    if (!filter) {
      sessionStorage.removeItem(filterSessionKey);
      setFilters(defaultFilters);
      setSelectedFilterId(null);
      setTabFilters((prev) => ({
        ...prev,
        [activeTab]: defaultFilters,
      }));
      setSearchText("");
      setCurrentPage(1);
      return;
    }

    const newFilters = filter.filters || {};
    setFilters(newFilters);
    setSelectedFilterId(filter.id);
    setTabFilters((prev) => ({
      ...prev,
      [activeTab]: newFilters,
    }));
    setSearchText(newFilters.textfields || "");
    setCurrentPage(1);

    // Save to session
    const filterToStore = {
      filters: newFilters,
      activeTab,
      searchText: newFilters.textfields || "",
      selectedFilterId: filter.id, // store applied filter ID
    };
    sessionStorage.setItem(filterSessionKey, JSON.stringify(filterToStore));
  };

  // Callback from MoreFilterDropdown
  const handleOpenRelatedDropdown = (relatedArray) => {
    setActiveRelatedDropdowns(relatedArray || []);

    const unselected = ["ContactNameDropdown", "ContactEmailDropdown"].filter(
      (d) => !relatedArray.includes(d),
    );

    unselected.forEach((dropdown) => {
      if (dropdown === "ContactNameDropdown") {
        updateFilter("primaryContactId", "");
        setMoreFilterSelectedIds((prev) => prev.filter((id) => id !== "1"));
      }
      if (dropdown === "ContactEmailDropdown") {
        updateFilter("primaryContactEmail", { value: "" });
        setMoreFilterSelectedIds((prev) => prev.filter((id) => id !== "2"));
      }
    });
  };

  const handleClear = () => {
    // Reset filters to default
    // setFilters({});
    setFilters(defaultFilters);
    setEmails([]);
    setContactEmails([]);
    // Clear checkboxes and dropdown states
    setMoreFilterSelectedIds([]);
    setActiveRelatedDropdowns([]);
    // Clear search text
    setSearchText("");
    // Clear selected filter
    setSelectedFilterId(null);
    // Reset tabFilters for current tab
    setTabFilters((prev) => ({
      ...prev,
      [activeTab]: defaultFilters,
    }));
    // Remove session storage entry
    sessionStorage.removeItem(filterSessionKey);
    // Reset pagination
    setCurrentPage(1);
  };

  // Debounce effect
  useEffect(() => {
    // 🧠 If searchText is empty → reset filters immediately
    if (searchText.trim() === "") {
      setFilters((prev) => ({
        ...prev,
        textfields: "",
      }));
      setCurrentPage(1);
      return; // skip debounce logic
    }

    // 🕒 Otherwise run debounce when user types
    const handler = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        textfields: searchText,
      }));
      setCurrentPage(1);
    }, 500);

    // 🧹 Cleanup debounce
    return () => clearTimeout(handler);
  }, [searchText]);

  const openSaveFilterModal = () => setOpenModalView(true);

  const getAddressBookData = async () => {
    try {
      const url = `/v1/addressbook/${currentProject?.id}/${projectType.property}`;
      const response = await apiGet(url);
      const mergedData = response.data;
      setData(mergedData);
    } catch (error) {
      console.log("Error", error);
    }
  };

  useEffect(() => {
    getAddressBookData();
  }, [currentProject?.id]);

  const handleAdvancedOptions = () => setHideFilterOptions(!hideFilterOptions);

  const updateFilter = (filterKey, updates) => {
    console.log("updateFilter", filterKey, updates);
    setFilters((prev) => {
      const updatedFilters = updates
        ? { ...prev[filterKey], ...updates }
        : null;

      setTabFilters((prevTabs) => ({
        ...prevTabs,
        [activeTab]: {
          ...prevTabs[activeTab],
          [filterKey]: updatedFilters,
        },
      }));

      return { ...prev, [filterKey]: updatedFilters };
    });
  };

  // Helper to map dropdown ID to relatedDropdown key
  const getDropdownKeyById = (id) => {
    const map = {
      1: "ContactNameDropdown",
      2: "ContactEmailDropdown",
    };
    return map[id];
  };

  useEffect(() => {
    // Restore saved filters for the selected tab
    const saved = tabFilters[activeTab] || filters;
    setFilters(saved);
    // Fetch saved filters from backend
    fetchSavedFilters(activeTab, type, occupantsID);
    // Reset page
    setCurrentPage(1);
  }, [activeTab]);

  const getFilterData = async () => {
    setIsLoading(true);
    try {
      const searchQuery = buildSearchQuery() || "";
      if (!currentProject?.id) {
        setOccupantData([]);
        setTotalRecord(0);
        return;
      }
      const payload = {
        projectId: currentProject?.id,
        searchQuery,
        pageSize,
        pageNo: currentPage,
      };
      const response = await apiPost(
        `/v1/occupants/advancedsearch`,
        payload,
        false,
      );
      setOccupantData(response || []);
      setTotalRecord(response?.totalCount || 0);
    } catch (error) {
      console.error("Error fetching document data:", error);
      setOccupantData([]);
      setTotalRecord(0);
    } finally {
      setIsLoading(false);
    }
  };

  // useEffect(() => {
  //   getFilterData();
  // }, [filters, pageSize, currentPage, currentProject?.id]);

  useEffect(() => {
    getFilterData();
  }, [filters, pageSize, currentPage]);

  const buildSearchQuery = () => {
    const conditions = [];

    // Multi-select filters
    const multiSelectFilters = [
      {
        field: "id",
        header: filters?.occupants?.header,
        selectedIds: filters?.occupants?.selectedIds,
      },
      {
        field: "tenantStatus",
        header: filters?.tenantStatus?.header,
        selectedIds: filters?.tenantStatus?.selectedIds,
      },
      {
        field: "primaryContactId",
        header: filters?.primaryContactId?.header,
        selectedIds: filters?.primaryContactId?.selectedIds,
      },
      {
        field: "lastUpdateBy",
        header: filters?.lastUpdateBy?.header,
        selectedIds: filters?.lastUpdateBy?.selectedIds,
      },
      {
        field: "coordinatorEmail", // ✅ handle coordinatorEmail here as multi-select
        header: filters?.coordinatorEmail?.header,
        selectedIds: filters?.coordinatorEmail?.value,
      },
      {
        field: "primaryContactEmail", // ✅ handle primaryContactEmail here as multi-select
        header: filters?.primaryContactEmail?.header,
        selectedIds: filters?.primaryContactEmail?.value,
      },
    ];

    multiSelectFilters.forEach(({ field, header, selectedIds }) => {
      if (!selectedIds?.length) return;
      const operator = header === "equals" ? "IN" : "NOT IN";

      // Detect if IDs are numeric
      const isNumeric = selectedIds.every((id) => !isNaN(Number(id)));

      const formattedIds = selectedIds
        .map((id) => (isNumeric ? id : `"${id}"`))
        .join(",");

      conditions.push(`${field} ${operator} (${formattedIds})`);
    });

    // Text filters
    const textFilters = [
      //   { field: "note", ...filters.submissionNote },
      // { field: "subject", ...filters.subject },
    ];

    const addTextCondition = (field, value, header) => {
      if (!value) return;
      switch (header) {
        case "equals":
          conditions.push(`${field} = "${value}"`);
          break;
        case "not-equals":
          conditions.push(`${field} != "${value}"`);
          break;
        case "contains":
          conditions.push(`${field} ~ "${value}"`);
          break;
        case "startsWith":
          conditions.push(`${field} STARTS WITH "${value}"`);
          break;
        case "endsWith":
          conditions.push(`${field} ENDS WITH "${value}"`);
          break;
        default:
          break;
      }
    };

    textFilters.forEach(({ field, value, header }) =>
      addTextCondition(field, value, header),
    );

    // TextFields quick search
    if (filters.textfields && filters.textfields.trim() !== "") {
      conditions.push(`textfields ~ "${filters.textfields.trim()}"`);
    }

    // --- Date filters: CreatedOn & LastUpdate ---
    ["occupantOpeningDate", "lastUpdate"].forEach((field) => {
      const val = filters[field];
      if (!val) return; // skip if null

      let conditionStr = "";

      switch (val.header) {
        case "withinLast":
          if (val.value?.number) {
            conditionStr = `${field} last ${val.value.number} ${val.value.unit}`;
          }
          break;

        case "moreThan":
          if (val.value?.number) {
            conditionStr = `${field} moreThan ${val.value.number} ${val.value.unit}`;
          }
          break;

        case "inRange":
          if (val.value?.[0] && val.value?.[1]) {
            conditionStr = `${field} inRange "${val.value[0]}" "${val.value[1]}"`;
          }
          break;

        default:
          break;
      }

      if (conditionStr) conditions.push(conditionStr);
    });

    // --- Revision filter ---
    if (filters.revision) {
      const revHeader = filters.revision.value;
      switch (revHeader) {
        case "latest":
          conditions.push("revision = latest");
          break;
        case "all":
          conditions.push("revision = all");
          break;
        default:
          break;
      }
    }

    // Assuming filters is an object with keys representing dynamic fields
    Object.keys(filters || {}).forEach((key) => {
      const filter = filters[key];
      if (
        filter?.value !== null &&
        filter?.value !== "" &&
        Number(filter?.value) > 0
      ) {
        // Determine operator dynamically
        let operator = "="; // default
        switch (filter.header) {
          case "not-equals":
            operator = "!=";
            break;
          case "lessThanEqual":
            operator = "<=";
            break;
          case "greaterThanEqual":
            operator = ">=";
            break;
          // Add more operators if needed
        }

        // Push condition dynamically using the field name
        conditions.push(`${key} ${operator} ${filter.value}`);
      }
    });

    // --- Status filter ---
    if (filters.status) {
      conditions.push(`status = ${filters.status.value}`);
    }

    // Additional advanced filters: allow raw JQL conditions from frontend
    if (filters.rawJQL && filters.rawJQL.length) {
      conditions.push(...filters.rawJQL);
    }

    return conditions.join(" AND ");
  };

  const deriveSelectedIdsFromFilters = () => {
    const ids = [];
    if (filters.primaryContactId?.selectedIds?.length > 0) ids.push("1"); // ContactNameDropdown
    if (filters.primaryContactEmail?.value) ids.push("2"); // EmailFilterDropdown
    return ids;
  };

  // Sync checkboxes when filters are first loaded or a filter is applied
  useEffect(() => {
    const selectedIdsFromFilters = deriveSelectedIdsFromFilters();
    setMoreFilterSelectedIds(selectedIdsFromFilters);

    const relatedDropdowns = selectedIdsFromFilters.map(getDropdownKeyById);
    setActiveRelatedDropdowns(relatedDropdowns);
  }, [selectedFilterId]); // only when a filter is applied/loaded

  const handleMoreFilterChange = (ids) => {
    const removedIds = moreFilterSelectedIds.filter((id) => !ids.includes(id));

    removedIds.forEach((id) => {
      const dropdownKey = getDropdownKeyById(id);
      if (dropdownKey === "ContactNameDropdown")
        updateFilter("primaryContactId", { selectedIds: [] });
      else if (dropdownKey === " ContactEmailDropdown")
        updateFilter("primaryContactEmail", { value: "" });
    });

    setMoreFilterSelectedIds(ids);
    setActiveRelatedDropdowns(ids.map(getDropdownKeyById));
  };

  return (
    <>
      <SC.Container>
        {/* Search Row */}
        <SC.SearchRow>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              width: "45%",
            }}
          >
            <SC.InputContiner>
              <SC.SearchIcon />
              <SC.InputStyle
                placeholder="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value.trimStart())}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchText.trim() !== "") {
                    getFilterData();
                  }
                }}
              />
            </SC.InputContiner>

            <SC.AdvancedOptionsButton onClick={handleAdvancedOptions}>
              Advanced Options
              {appliedFilterCount > 0 && (
                <SC.Badge>{appliedFilterCount}</SC.Badge>
              )}
              {hideFilterOptions ? (
                <InnoIcons.KeyboardArrowUpIcon />
              ) : (
                <InnoIcons.KeyboardArrowDownIcon />
              )}
            </SC.AdvancedOptionsButton>
          </div>

          {hideFilterOptions && savedFilters.length > 0 && (
            <SavedFilterDropdown
              label=""
              options={savedFilters}
              selectedId={selectedFilterId}
              onSelectionChange={(filter) => {
                applyFilter(filter);
                setSelectedFilterId(filter.id);
              }}
              handleClear={() => {
                applyFilter(null);
                setSelectedFilterId(null);
                handleClear();
              }}
            />
          )}
        </SC.SearchRow>

        {/* Advanced Filter Section */}

        {hideFilterOptions && (
          <SC.FilterSection>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                // marginBottom: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <SC.FilterRow>
                  <SC.FilterTitle>Advanced Filter</SC.FilterTitle>
                  <MultiSelectDropdown
                    label="Occupant"
                    headerOptionsValueName={"Occupant"} // e.g., equals / not-equals
                    selectedHeaderId={filters?.occupants?.header}
                    onHeaderChange={(header) =>
                      updateFilter("occupants", { header })
                    }
                    options={options} // array of { id, label }
                    selectedIds={filters?.occupants?.selectedIds}
                    onSelectionChange={(selectedIds) =>
                      updateFilter("occupants", { selectedIds })
                    }
                  />

                  <MultiSelectDropdown
                    label="Tenant Status"
                    headerOptionsValueName={"Tenant Status"} // e.g., equals / not-equals
                    selectedHeaderId={filters?.tenantStatus?.header}
                    onHeaderChange={(header) =>
                      updateFilter("tenantStatus", { header })
                    }
                    options={tenantReleaseStatus} // array of { id, label }
                    selectedIds={filters?.tenantStatus?.selectedIds}
                    onSelectionChange={(selectedIds) =>
                      updateFilter("tenantStatus", { selectedIds })
                    }
                  />
                  <OccupantAreaFilterDropdown
                    label="Occupant Area"
                    selectedHeaderId={filters?.occupantArea?.header}
                    headerOptionsValueName="Occupant Area"
                    onHeaderChange={(header) =>
                      updateFilter("occupantArea", { header })
                    }
                    selectedValue={filters?.occupantArea?.value || ""}
                    onValueChange={(valueInM2) =>
                      updateFilter("occupantArea", { value: valueInM2 })
                    }
                  />
                  <NumberFilterDropdown
                    label="Outstanding Issues"
                    selectedHeaderId={filters?.countIssue?.header}
                    headerOptionsValueName="Outstanding issues"
                    onHeaderChange={(header) =>
                      updateFilter("countIssue", { header })
                    }
                    selectedValue={filters?.countIssue?.value || ""}
                    onValueChange={(valueInM2) =>
                      updateFilter("countIssue", { value: valueInM2 })
                    }
                  />

                  <CreatedOnDropdown
                    label="Opening Date"
                    field="occupantOpeningDate"
                    value={filters.occupantOpeningDate}
                    onUpdate={(value) =>
                      updateFilter("occupantOpeningDate", value)
                    }
                  />

                  <NumberFilterDropdown
                    label="Active Journals"
                    selectedHeaderId={filters?.journalActive?.header}
                    headerOptionsValueName="Active Journals"
                    onHeaderChange={(header) =>
                      updateFilter("journalActive", { header })
                    }
                    selectedValue={filters?.journalActive?.value || ""}
                    onValueChange={(valueInM2) =>
                      updateFilter("journalActive", { value: valueInM2 })
                    }
                  />

                  <EmailFilterDropdown
                    label="Coordinator"
                    headerOptionsValuename={"Coordinator"}
                    selectedHeaderId={filters?.coordinatorEmail?.header}
                    value={filters?.coordinatorEmail?.value}
                    onHeaderChange={(header) =>
                      updateFilter("coordinatorEmail", { header })
                    }
                    onUpdate={({ headerId, value }) =>
                      updateFilter("coordinatorEmail", {
                        header: headerId,
                        value,
                      })
                    }
                    emails={emails}
                    setEmails={setEmails}
                  />

                  <MoreFilterDropdown
                    label="More Filter"
                    selectedIds={moreFilterSelectedIds}
                    onSelectionChange={handleMoreFilterChange}
                    onOpenRelatedDropdown={handleOpenRelatedDropdown}
                    options={optionsMoreFilter}
                  />
                  {(activeRelatedDropdowns || []).includes(
                    "ContactNameDropdown",
                  ) && (
                    <MultiSelectDropdown
                      label="Contact Name"
                      headerOptionsValueName={"Contact Name"}
                      selectedHeaderId={filters?.primaryContactId?.header}
                      onHeaderChange={(header) =>
                        updateFilter("primaryContactId", { header })
                      }
                      options={userOptions}
                      selectedIds={filters?.primaryContactId?.selectedIds}
                      onSelectionChange={(selectedIds) =>
                        updateFilter("primaryContactId", { selectedIds })
                      }
                    />
                  )}
                  {(activeRelatedDropdowns || []).includes(
                    "ContactEmailDropdown",
                  ) && (
                    <EmailFilterDropdown
                      label="Contact Email"
                      headerOptionsValuename={"Contact Email"}
                      selectedHeaderId={filters?.primaryContactEmail?.header}
                      value={filters?.primaryContactEmail?.value}
                      onHeaderChange={(header) =>
                        updateFilter("primaryContactEmail", { header })
                      }
                      onUpdate={({ headerId, value }) =>
                        updateFilter("primaryContactEmail", {
                          header: headerId,
                          value,
                        })
                      }
                      emails={contactEmails}
                      setEmails={setContactEmails}
                    />
                  )}

                  <SC.FilterActions>
                    <Button
                      caption="Clear Filter"
                      onClick={handleClear}
                      fontSize="12px"
                      style={{
                        background: theme.colors.cancelButton,
                        color: theme.colors.white,
                        minWidth: "90px",
                        borderRadius: "5px",
                        height: "35px",
                        fontSize: "12px",
                      }}
                    />
                    <Button
                      caption={"Save Filter"}
                      onClick={openSaveFilterModal}
                      color="white"
                      fontSize="12px"
                      style={{
                        background: theme.gradient.themeGradient,
                        color: theme.colors.white,
                        minWidth: "90px",
                        height: "35px",
                        borderRadius: "5px",
                      }}
                    />
                  </SC.FilterActions>
                </SC.FilterRow>
              </div>
            </div>
          </SC.FilterSection>
        )}
      </SC.Container>

      {occupantData && (
        <SC.CardContainer>
          <p style={{ fontWeight: 500 }}>
            Total Record: {occupantData?.totalCount || 0}{" "}
          </p>
          <OccupantTable
            data={occupantData}
            pageSize={pageSize}
            currentPage={currentPage}
            setPageSize={setPageSize}
            setCurrentPage={setCurrentPage}
            isLoading={isLoading}
          />
        </SC.CardContainer>
      )}

      {openModalView && (
        <SaveFilterModal
          openModalView={openModalView}
          onClose={() => setOpenModalView(false)}
          filters={filters}
          searchQuery={buildSearchQuery()}
          type={activeTab}
          projectId={currentProject?.id}
          fetchSavedFilters={fetchSavedFilters}
        />
      )}
    </>
  );
};

export default OccupantSearchInterface;
